import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronRight, ShieldCheck, Truck, Lock, RotateCcw, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import api from "../services/api";
import LicensePlateInput from "../components/LicensePlateInput";
import { Reveal, Stagger, Item } from "../components/fx/Reveal";
import { BrandMarquee, StatsStrip, HowItWorks, ProCta, Testimonials } from "../components/home/Sections";
import { useSettings } from "../context/SettingsContext";
import { RecentlyViewed } from "../components/Recommendations";
import { HeroFx } from "../components/fx/HeroFx";
import { CategoryTile, CategoryTileSkeleton } from "../components/home/CategoryTile";
import { ProductCarousel } from "../components/home/ProductCarousel";
import { TrustBanner } from "../components/home/TrustBanner";

const HERO_IMG = "https://images.unsplash.com/photo-1606577924006-27d39b132ae2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const LicensePlate = () => {
  const navigate = useNavigate();
  const [plate, setPlate] = useState("");
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8, delay: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
      className="relative glass-panel rounded-2xl p-7 md:p-9 max-w-[1040px] mx-auto overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E01F1F]/25 blur-3xl pointer-events-none" />
      <div className="scanline" />
      <div className="flex items-center gap-3 mb-5 relative">
        <span className="h-6 w-1.5 bg-[#E01F1F] rounded-full shadow-[0_0_12px_rgba(224,31,31,0.8)]" />
        <h3 className="text-white font-extrabold italic uppercase tracking-tight text-xl md:text-2xl">Entrez votre plaque d'immatriculation</h3>
      </div>
      <div className="flex flex-col md:flex-row gap-4 relative">
        <LicensePlateInput testid="home-plate-input" className="flex-1" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} />
        <button
          data-testid="home-plate-btn"
          onClick={() => navigate(plate ? `/vehicule?plate=${encodeURIComponent(plate)}` : "/vehicule")}
          className="btn-orange btn-shine rounded-xl px-8 py-4 font-bold text-lg whitespace-nowrap uppercase tracking-wide"
        >
          Identifier mon véhicule
        </button>
      </div>
      <p className="text-[13px] text-slate-500 mt-4 relative">Identification instantanée · Pièces 100 % compatibles avec votre véhicule</p>
    </motion.div>
  );
};

const trustFor = (s) => [
  { icon: ShieldCheck, title: "Pièces compatibles garanties", sub: "Qualité d'origine ou équivalente" },
  { icon: Truck, title: "Livraison rapide", sub: `Expédition en ${s.delivery_delay}` },
  { icon: Lock, title: "Paiement sécurisé", sub: "CB, PayPal, 3x ou 4x" },
  { icon: RotateCcw, title: `Retours sous ${s.return_days} jours`, sub: "Satisfait ou remboursé" }
];

const fadeUp = (d = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.2, 0.7, 0.2, 1] } });

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const padTiles = categories.length > 1 ? (4 - ((categories.length + 3) % 4)) % 4 : 0;
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [homePromo, setHomePromo] = useState(null);
  const settings = useSettings();
  const trust = trustFor(settings);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoadingProducts(false));
    api.get("/home-promo").then(({ data }) => setHomePromo(data)).catch(() => {});
  }, []);

  return (
    <div className="relative z-10">
      {/* Hero */}
      <section className="hero-swoosh">
        <motion.img
          initial={{ scale: 1.12, opacity: 0 }} animate={{ scale: 1, opacity: 0.22 }} transition={{ duration: 1.6, ease: "easeOut" }}
          src={HERO_IMG} alt="" aria-hidden="true" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity pointer-events-none"
        />
        <HeroFx />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090C]/30 via-transparent to-[#08090C] pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-5 pt-14 pb-16 relative z-10">
          <motion.div {...fadeUp(0)} className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E01F1F] animate-pulse shadow-[0_0_10px_#E01F1F]" /> + de 650 000 références · Pièces auto · Prix pro
            </span>
          </motion.div>
          <motion.h1 {...fadeUp(0.1)} className="text-center font-extrabold italic uppercase tracking-tight leading-[0.92] text-4xl sm:text-5xl md:text-7xl mb-10">
            <span className="text-chrome">Trouvez les bonnes pièces</span><br />
            <span className="text-white">pour votre </span><span className="text-[#FF3B3B] red-glow">véhicule</span>
          </motion.h1>
          <LicensePlate />
          <motion.div {...fadeUp(0.45)} className="text-center mt-7">
            <Link to="/vehicule" data-testid="home-search-by-model" className="inline-flex items-center gap-1.5 text-slate-200 font-bold text-lg hover:text-[#FF3B3B] transition-colors group">
              Rechercher par marque et modèle <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <StatsStrip />
        </div>
      </section>

      <BrandMarquee />

      {/* Categories – bento */}
      <section className="max-w-[1440px] mx-auto px-5 pt-20">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B3B] font-bold mb-3">Catalogue complet</div>
              <h2 className="speed-line font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl">Nos catégories</h2>
            </div>
            <Link to="/recherche?q=" className="hidden sm:flex text-slate-300 font-semibold hover:text-[#FF3B3B] transition-colors items-center gap-1">Tout le catalogue <ChevronRight size={18} /></Link>
          </div>
        </Reveal>
        <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] md:auto-rows-[210px]">
          {categories.length === 0 && [0, 1, 2, 3, 4].map((i) => <Item key={i} className={i === 0 ? "col-span-2 row-span-2" : ""}><CategoryTileSkeleton /></Item>)}
          {categories.map((c, i) => (
            <Item key={c.slug} className={i === 0 ? "col-span-2 row-span-2" : i >= categories.length - padTiles ? "col-span-2" : ""}>
              <CategoryTile category={c} index={i} large={i === 0} />
            </Item>
          ))}
        </Stagger>
      </section>

      {/* Trust badges */}
      <section className="max-w-[1440px] mx-auto px-5 mt-12">
        <Reveal>
          <div className="bg-gradient-to-br from-[#181C26] to-[#0D0F14] rounded-2xl border border-[#222735] px-6 py-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trust.map((t, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#08090C] border border-[#2A3040] flex items-center justify-center shrink-0 shadow-inner">
                  <t.icon size={22} className="text-[#FF3B3B]" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-bold text-slate-50 text-[15px] leading-tight">{t.title}</div>
                  <div className="text-[13px] text-slate-400">{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <HowItWorks />

      <ProductCarousel products={products} loading={loadingProducts} />

      <TrustBanner />

      <ProCta />

      {/* Promo banner (piloté depuis Admin > Promotions) */}
      {homePromo && homePromo.active && homePromo.category && (
        <section className="max-w-[1440px] mx-auto px-5 mt-24" data-testid="home-promo-banner">
          <Reveal>
            <div className="rounded-3xl bg-[#10131A] border border-[#E01F1F]/40 px-8 py-10 md:px-14 md:py-14 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden shadow-2xl shadow-red-950/30">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(224,31,31,0.3),transparent_60%)] pointer-events-none" />
              <div className="absolute right-0 top-0 h-full w-1/3 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(115deg, transparent 0 30px, #fff 30px 32px)" }} />
              <div className="relative z-10">
                <div className="inline-block bg-[#E01F1F] text-white text-xs font-black italic uppercase tracking-wider px-3 py-1 rounded mb-3 shadow-lg shadow-red-950/50">
                  PROMO{homePromo.discount_percent ? ` -${homePromo.discount_percent}%` : ""}
                </div>
                <h2 className="text-white font-extrabold italic uppercase tracking-tight text-3xl md:text-5xl leading-[0.95]">
                  {homePromo.title || `Sur tout le ${(homePromo.category_name || "").toLowerCase()}`}
                </h2>
                <p className="text-slate-400 mt-3">{homePromo.subtitle || "Pièces de grandes marques au meilleur prix."}</p>
              </div>
              <Link to={`/categorie/${homePromo.category}`} data-testid="home-promo-cta" className="btn-orange btn-shine relative z-10 rounded-xl px-8 py-4 font-bold text-lg uppercase tracking-wide inline-flex items-center gap-2">J'en profite <ArrowRight size={20} /></Link>
            </div>
          </Reveal>
        </section>
      )}

      <Testimonials />
      <div className="max-w-[1440px] mx-auto px-5 pb-16"><RecentlyViewed limit={4} /></div>
    </div>
  );
};

export default HomePage;
